import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import PostRequest from '../../../../service/PostRequest';
import BOSS_mainlogo from '../../../../assets/icons/BOSS_mainlogo.svg';
import './WhatsappResetPassword.scss';
import InputPassword from '../../../../components/Widgets/Inputs/InputPassword';

// Component for individual OTP input field
const OTPInput = ({ value, onChange, index }) => (
    <input
        type="text"
        maxLength="1"
        value={value}
        onChange={(e) => onChange(e.target.value, index)}
        className="otp-input"
        id={`otp-input-${index}`}
    />
);

const WhatsappResetPassword = () => {
    const [otp, setOtp] = useState(Array(6).fill("")); // 6-digit OTP
    const [phoneNumber, setPhoneNumber] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [otpError, setOtpError] = useState("");
    const [phoneNumberError, setPhoneNumberError] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [confirmPasswordError, setConfirmPasswordError] = useState("");
    const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
    const toastBottomCenter = useRef(null);

    const navigate = useNavigate();
    const location = useLocation();
    const resetQuery = new URLSearchParams(location.search).get('query');

    useEffect(() => {
        const phoneNumberFromQuery = new URLSearchParams(location.search).get('phoneNumber');
        if (phoneNumberFromQuery) {
            setPhoneNumber(phoneNumberFromQuery);
        }
    }, [location.search]);

    const passwordMinLength = 8;
    const passwordMaxLength = 8;
    const passwordHasSpecialChar = /[@$!%*?&]/;
    const passwordInvalidChar = /[^A-Za-z\d@$!%*?&]/;

    const validateInputs = () => {
        let isValid = true;

        if (otp.join("").trim().length < 6) {
            setOtpError("OTP is required");
            isValid = false;
        } else {
            setOtpError("");
        }

        if (!phoneNumber.trim()) {
            setPhoneNumberError("Phone Number is required");
            isValid = false;
        } else {
            setPhoneNumberError("");
        }

        if (!password.trim()) {
            setPasswordError("Password is required");
            isValid = false;
        } else if (password.length !== passwordMaxLength) {
            setPasswordError(`Password must be exactly ${passwordMaxLength} characters long`);
            isValid = false;
        } else if (!passwordHasSpecialChar.test(password)) {
            setPasswordError("Password must contain at least one special character (@$!%*?&)");
            isValid = false;
        } else if (passwordInvalidChar.test(password)) {
            setPasswordError("Password contains invalid characters");
            isValid = false;
        } else {
            setPasswordError("");
        }

        if (!confirmPassword.trim()) {
            setConfirmPasswordError("Confirm Password is required");
            isValid = false;
        } else if (password !== confirmPassword) {
            setPasswordError("Passwords do not match");
            setConfirmPasswordError("Passwords do not match");
            isValid = false;
        } else {
            setConfirmPasswordError("");
        }

        return isValid;
    };

    const handleSubmit = async () => {
        if (!validateInputs()) {
            return;
        }

        try {
            const otpString = otp.join("");
            const data = {
                otp: otpString,
                phoneNumber: `+${phoneNumber}`,
                password: password,
            };

            setIsWaitingForResponse(true);
            const response = await PostRequest(`auth/reset/password/whatsapp?resetPasswordQuery=${resetQuery}`, data, false);
            
            if (response.status === 204) {
                setIsWaitingForResponse(false);
                showMessage("Your password is reset successfully", toastBottomCenter, 'success');
                setTimeout(() => {
                    navigate("/whatsapp-login");
                }, 2000);
            } else if (response) {
                setIsWaitingForResponse(false);
                navigate("/login");
            } else {
                setIsWaitingForResponse(false);
                setPasswordError("Failed to reset password. Please try again.");
                showMessage("Failed to reset password", toastBottomCenter, 'danger');
            }
        } catch (error) {
            console.error("Error resetting password:", error);
            setIsWaitingForResponse(false);
            setPasswordError("Failed to reset password. Please try again.");
            showMessage("Error resetting password", toastBottomCenter, 'danger');
        }
    };

    const showMessage = (message, ref, severity) => {
        ref.current.show({ severity: severity, summary: message, detail: message, life: 3000 });
    };

    // Update OTP state on input change
    const handleOtpChange = (value, index) => {
        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        // Move focus to next input
        if (value && index < otp.length - 1) {
            document.getElementById(`otp-input-${index + 1}`).focus();
        }
    };

    return (
        <div className='ResetPasswordContainer'>
            <div className='Heading'>
                <img src={BOSS_mainlogo} alt="BOSS Logo" />
                <h3>Update Your Password</h3>
            </div>
            <div className='WholeForm'>
                <div className='PhoneNumber'>
                    <h3>+{phoneNumber}</h3>
                </div>
                <div className='OtpInput'>
                    <label>Enter OTP</label>
                    <div className='otp-container'>
                        {otp.map((digit, index) => (
                            <OTPInput
                                key={index}
                                value={digit}
                                onChange={handleOtpChange}
                                index={index}
                            />
                        ))}
                    </div>
                    {otpError && <div className="error-message">{otpError}</div>}
                </div>
                <div className='PasswordInput'>
                    <label>Enter New Password</label>
                    <InputPassword value={password} onChange={(e) => setPassword(e.target.value)} label="Enter Your Password" errorMessage={passwordError} />
                    {passwordError && <div style={{color:"red", width:"340px", marginTop:"5px"}}>{passwordError}</div>}
                </div>
                <div className='PasswordInput'>
                    <label>Confirm New Password</label>
                    <InputPassword value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} label="Confirm Your Password" errorMessage={confirmPasswordError} />
                    {confirmPasswordError && <div className="error-message">{confirmPasswordError}</div>}
                </div>
                <Button label="Reset Password" onClick={handleSubmit} style={{ width: "355px", borderRadius: "40px", margin: "30px 0px" }} disabled={isWaitingForResponse} loading={isWaitingForResponse} />
            </div>
            <Toast ref={toastBottomCenter} position="bottom-center" />
        </div>
    );
};

export default WhatsappResetPassword;
